import React, { useState } from "react";

interface TermsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept: () => void;
  color1?: string;
  actionText?: string; // "Continue with Google" or "Create Account"
}

const TermsModal: React.FC<TermsModalProps> = ({
  isOpen,
  onClose,
  onAccept,
  color1 = "#1e1b4b",
  actionText = "Continue",
}) => {
  const [termsAccepted, setTermsAccepted] = useState(false);

  const isWhiteOrShade = (color: string): boolean => {
    if (!color) return false;
    if (color.toLowerCase() === "white" || color === "rgba(255,255,255,1)") return true;
    if (/^#fff[a-f0-9]{0,3}$/i.test(color)) return true;
    const rgba = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+),?\s*([\d.]+)?\)/);
    if (rgba) {
      const [, r, g, b] = rgba.map(Number);
      return r >= 240 && g >= 240 && b >= 240;
    }
    return false;
  };

  const linkColor = isWhiteOrShade(color1) ? "#000" : color1;

  const handleAccept = () => {
    if (termsAccepted) {
      setTermsAccepted(false); // Reset for next use
      onAccept();
    }
  };

  const handleClose = () => {
    setTermsAccepted(false); // Reset on close
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
      }}
      onClick={handleClose}
    >
      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: "12px",
          padding: "24px",
          maxWidth: "450px",
          width: "90%",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          style={{
            margin: "0 0 16px 0",
            fontSize: "18px",
            fontWeight: "600",
            color: "#333",
          }}
        >
          Terms & Conditions
        </h3>
        <p
          style={{
            margin: "0 0 20px 0",
            fontSize: "14px",
            color: "#666",
            lineHeight: "1.5",
          }}
        >
          Before continuing, please review and accept our Terms & Conditions and Privacy Policy.
        </p>

        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "12px",
            marginBottom: "24px",
          }}
        >
          <input
            type="checkbox"
            id="terms-modal-checkbox"
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            style={{
              marginTop: "2px",
              width: "18px",
              height: "18px",
              cursor: "pointer",
              accentColor: linkColor,
            }}
          />
          <label
            htmlFor="terms-modal-checkbox"
            style={{
              fontSize: "14px",
              color: "#333",
              cursor: "pointer",
              lineHeight: "1.4",
            }}
          >
            I agree to the{" "}
            <a
              href="https://mypropertygroup.net/terms-conditions-2025"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: linkColor, textDecoration: "underline" }}
            >
              Terms & Conditions
            </a>{" "}
            and{" "}
            <a
              href="https://mypropertygroup.net/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: linkColor, textDecoration: "underline" }}
            >
              Privacy Policy
            </a>
          </label>
        </div>

        <div
          style={{
            display: "flex",
            gap: "12px",
            justifyContent: "flex-end",
          }}
        >
          <button
            type="button"
            onClick={handleClose}
            style={{
              padding: "10px 20px",
              borderRadius: "6px",
              border: "1px solid #ddd",
              backgroundColor: "#fff",
              color: "#666",
              fontSize: "14px",
              fontWeight: "500",
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleAccept}
            disabled={!termsAccepted}
            style={{
              padding: "10px 20px",
              borderRadius: "6px",
              border: "none",
              backgroundColor: termsAccepted ? linkColor : "#ccc",
              color: "#fff",
              fontSize: "14px",
              fontWeight: "500",
              cursor: termsAccepted ? "pointer" : "not-allowed",
              opacity: termsAccepted ? 1 : 0.7,
            }}
          >
            {actionText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TermsModal;
